import { compactFlatten } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getLocalCalendarDate, parseCalendarDate } from '@/Client/Lib/calendarDateLib';
import { useBulkPhotoUploader } from '@/lib/photoLib';
import { formatDate, groupNamePresenter, photoTagsPresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import { usePhotoFullSizeUri, useScanEditingContextSlice, useScanIsEditableCheck } from '@kaiizen/kai-pro-api-react-state';
import {
  getLatestScanPhotos,
  getScanGroup,
  getScanPart,
  getScanPhotosWithoutPhotoId,
  getScanTask,
  getScanWithUpdatedScanPart,
  ScanPhoto,
} from '@kaiizen/kai-scan-template-system';

import { Button, ZoomableImageViewer } from '@/Client/Widgets';
import { DRAG_OVERLAY_STYLE, usePhotoDropZone } from '@/Client/Widgets/PhotoDropZone';
import { useUser } from '@kaiizen/kai-pro-api-react-state';
import { useSearchParams } from 'react-router';
import nextIcon from '../../Assets/next.svg';
import prevIcon from '../../Assets/prev.svg';
import { CancelIcon, UploadIndicatorIcon } from '../../Icons';
import { useNavigateToPhoto, useToast } from '../../State';
import { useConfirmationModal } from '../../State/ConfirmationModalContext';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';

const PHOTO_VIEWER_CONTAINER = css`
  label: full-photo-viewer-container;
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0; /* never let variable-length content (title, button labels) drive the width of the parent layout */
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--background2);
  padding: ${LAYOUT2.spacing2}px;
`;

const PHOTO_VIEWER_PHOTO_SECTION = css`
  position: relative;
  label: FULL_PHOTO_VIEWER_PHOTO_SECTION;
  width: 100%;
  height: 100%;
  flex-grow: 1;
  background-color: ${COLORS2.alwaysDark.background.black};
`;

const PHOTO_VIEWER_UPLOAD_INDICATOR_STYLE = css`
  label: PHOTO_VIEWER_UPLOAD_INDICATOR_STYLE;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 12;
  pointer-events: none;
`;

const BUTTON_BASE_STYLE = css`
  position: relative;
  width: 45px;
  height: 45px;
  background-color: ${COLORS2.alwaysDark.background.light};
  color: ${COLORS2.alwaysDark.text.white};
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 10;
  &:hover {
    background-color: ${COLORS2.alwaysDark.background.dark};
    transform: scale(1.1);
  }
`;

const CLOSE_BUTTON_STYLE = css`
  label: CLOSE_BUTTON_STYLE;
`;

const NAV_BUTTON_STYLE = css`
  label: NAV_BUTTON_STYLE;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 53px;
  height: 71px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PREV_BUTTON_STYLE = css`
  label: PREV_BUTTON_STYLE;
  left: 0px;
`;

const NEXT_BUTTON_STYLE = css`
  label: NEXT_BUTTON_STYLE;
  right: 0px;
`;

const PHOTO_VIEWER_TITLE_SECTION = css`
  label: FULL_PHOTO_VIEWER_TITLE_SECTION;
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  padding-bottom: ${LAYOUT2.spacing2}px;
`;

const TRUNCATE_WITH_ELLIPSIS_STYLE = css`
  label: TRUNCATE_WITH_ELLIPSIS_STYLE;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PHOTO_TITLE_INFO_STYLE = css`
  label: FULL_PHOTO_VIEWER_TITLE_INFO_STYLE;
  ${TYPOGRAPHY2.xLarge}
  flex-grow: 1;
  min-width: 0;
`;

const PHOTO_TITLE_INFO_LINE1_STYLE = css`
  label: FULL_PHOTO_VIEWER_TITLE_INFO_LINE1_STYLE;
  ${TYPOGRAPHY2.xLarge}
  ${TRUNCATE_WITH_ELLIPSIS_STYLE}
`;

const PHOTO_TITLE_INFO_LINE2_STYLE = css`
  label: FULL_PHOTO_VIEWER_TITLE_INFO_LINE2_STYLE;
  ${TYPOGRAPHY2.xSmall}
  ${TRUNCATE_WITH_ELLIPSIS_STYLE}
`;

const PHOTO_TITLE_BUTTONS_STYLE = css`
  label: FULL_PHOTO_VIEWER_TITLE_BUTTONS_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  flex-shrink: 0;
`;

const PHOTO_VIEWER_BOTTOM_SECTION = css`
  label: FULL_PHOTO_VIEWER_BOTTOM_SECTION;
  width: 100%;
  min-height: 50px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  padding-top: ${LAYOUT2.spacing2}px;
  gap: ${LAYOUT2.spacing1}px;
`;

const PHOTO_VIEWER_BOTTOM_SECTION_LEFT_CONTENT_STYLE = css`
  label: PHOTO_VIEWER_BOTTOM_SECTION_LEFT_CONTENT_STYLE;
  flex-grow: 1;
  min-width: min(200px, 100%);
  display: flex;
`;

const PHOTO_VIEWER_BOTTOM_SECTION_RIGHT_CONTENT_STYLE = css`
  label: PHOTO_VIEWER_BOTTOM_SECTION_RIGHT_CONTENT_STYLE;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${LAYOUT2.spacing1}px;
`;

const ERROR_IMAGE_CONTAINER_STYLE = css`
  label: ERROR_IMAGE_CONTAINER_STYLE;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${LAYOUT2.spacing2}px;
  color: ${COLORS2.alwaysDark.text.white};
`;

const ERROR_MESSAGE_STYLE = css`
  label: ERROR_MESSAGE_STYLE;
  ${TYPOGRAPHY2.medium}
  color: ${COLORS2.alwaysDark.text.white};
`;

const getNextPhotoIndex = (index: number, numPhotos: number) => {
  return (index + 1) % numPhotos;
};

const getPrevPhotoIndex = (index: number, numPhotos: number) => {
  return (index - 1 + numPhotos) % numPhotos;
};

type ZoomMode = 'fit' | 'zoomed';

type PendingZoomScroll = { left: number; top: number };

export type ShowScanPhotoProps = {
  photoId: string;
  groupId?: string;
  taskId?: string;
};

const usePersistentValidPhotoIndex = (photos: ScanPhoto[], photoId?: string) => {
  const [lastValidIndex, setLastValidIndex] = useState(0);

  const photoIndex = photoId ? photos.findIndex(photo => photo.photoId === photoId) : -1;
  const rawIndex = photoIndex >= 0 ? photoIndex : lastValidIndex;
  const clamped = rawIndex < 0 ? 0 : rawIndex >= photos.length ? Math.max(0, photos.length - 1) : rawIndex;

  useEffect(() => {
    if (clamped !== lastValidIndex) setLastValidIndex(clamped);
  }, [clamped, lastValidIndex]);

  return clamped;
};

const EMPTY_PHOTOS: ScanPhoto[] = [];
export const ScanLargePhotoViewer = ({
  closePhotos,
}: {
  closePhotos: () => void;
}) => {
  const navigateToPhoto = useNavigateToPhoto();
  const { scanRoot, scanIsEditable, scanDataMutators, scanId } = useScanEditingContextSlice(['scanRoot', 'scanIsEditable', 'scanDataMutators', 'scanId']);
  const [hasImageError, setHasImageError] = useState(false);

  // use query params to get the photosGroupId and photosTaskId and photoId
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('photosGroupId') ?? undefined;
  const taskId = searchParams.get('photosTaskId') ?? undefined;
  let photoId = searchParams.get('photoId') ?? undefined;
  const filterDate = parseCalendarDate(searchParams.get('photosDate') ?? '');

  const group = scanRoot && groupId ? getScanGroup(scanRoot, groupId) : undefined;
  const task = scanRoot && group && taskId ? getScanTask(scanRoot, group.id, taskId) : undefined;

  const scanPart = group ?? task ?? scanRoot;

  const ref = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const checkEditableWithToast = useScanIsEditableCheck(showToast);
  const confirm = useConfirmationModal();
  const unsortedPhotos = (!groupId && !taskId ? scanRoot?.photos : (task ?? group)?.optimized.allPhotos) ?? EMPTY_PHOTOS;

  const photos = useMemo(() => {
    const photosWithIndexedPhotos = unsortedPhotos.map((photo, index) => ({ ...photo, index }));
    photosWithIndexedPhotos.sort((a, b) => {
      if (!a.addedAt && !b.addedAt) return a.index - b.index;
      if (!a.addedAt) return 1;
      if (!b.addedAt) return -1;
      return a.addedAt - b.addedAt;
    });
    if (!filterDate) return photosWithIndexedPhotos;
    return photosWithIndexedPhotos.filter(photo => photo.addedAt && getLocalCalendarDate(photo.addedAt) === filterDate);
  }, [unsortedPhotos, filterDate]);

  const currentIndex = usePersistentValidPhotoIndex(photos, photoId);
  photoId = photos[currentIndex]?.photoId;
  const photo: ScanPhoto | undefined = photos[currentIndex];
  const nextIndex = getNextPhotoIndex(currentIndex, photos.length);
  const prevIndex = getPrevPhotoIndex(currentIndex, photos.length);
  const nextPhoto: ScanPhoto | undefined = photos[nextIndex] ?? photo;
  const prevPhoto: ScanPhoto | undefined = photos[prevIndex] ?? photo;

  const imageUri = usePhotoFullSizeUri(photoId);

  const hasMultiplePhotos = photos.length > 1;
  const bulkPhotoUploader = useBulkPhotoUploader();
  const { user } = useUser(photo?.addedByUserId);
  const { isDragOver, handlers: dropZoneHandlers } = usePhotoDropZone(
    scanPart,
    { groupId, taskId },
    photos => navigateToPhoto({ groupId, taskId, photoId: photos[photos.length - 1].photoId }),
  );

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!scanRoot || !scanPart) return;
    const photos = await bulkPhotoUploader(event.target.files, scanPart, { groupId, taskId });
    if (photos?.length) setTimeout(() => navigateToPhoto({ groupId, taskId, photoId: photos[photos.length - 1].photoId }), 100);
  }, [scanRoot, scanPart, groupId, taskId, bulkPhotoUploader, navigateToPhoto]);

  const goToPrevious = useCallback(() => {
    setHasImageError(false);
    navigateToPhoto({ groupId, taskId, photoId: prevPhoto.photoId });
  }, [groupId, taskId, prevPhoto, navigateToPhoto]);

  const goToNext = useCallback(() => {
    setHasImageError(false);
    navigateToPhoto({ groupId, taskId, photoId: nextPhoto.photoId });
  }, [groupId, taskId, nextPhoto, navigateToPhoto]);

  // Reset error state when photoId changes
  useEffect(() => {
    setHasImageError(false);
  }, [photoId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          if (hasMultiplePhotos) {
            event.preventDefault();
            goToPrevious();
          }
          break;
        case 'ArrowRight':
          if (hasMultiplePhotos) {
            event.preventDefault();
            goToNext();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasMultiplePhotos, goToPrevious, goToNext]);

  const removeFromGroup = () => {
    if (!checkEditableWithToast() || !scanRoot || !scanPart || !photoId || !group) return;

    confirm({
      title: 'Remove Photo',
      message: `Remove this photo from everywhere in the '${groupNamePresenter(group)}' group?`,
      onConfirm: () => {
        const uses = scanPart.optimized.allPhotoIdUsesByPhotoId[photoId];
        if (!uses || uses.length === 0) return;

        let updatingScanRoot = scanRoot;
        uses.forEach(use => {
          const { groupId, taskId, optionId, lineItemId } = use;
          const scanPart = getScanPart(updatingScanRoot, groupId, taskId, optionId, lineItemId);
          updatingScanRoot = getScanWithUpdatedScanPart(updatingScanRoot, groupId, taskId, optionId, lineItemId, {
            photos: getScanPhotosWithoutPhotoId(scanPart.photos, photoId),
          });
        });
        scanDataMutators.setScan(updatingScanRoot);
      },
    });
  };
  const removeFromScanRoot = () => {
    if (!checkEditableWithToast() || !scanRoot || !scanPart || !photoId || scanPart !== scanRoot) return;

    confirm({
      title: 'Remove Photo',
      message: 'Remove this photo from the scan root?',
      onConfirm: () => {
        scanDataMutators.updateScan({
          photos: getScanPhotosWithoutPhotoId(scanRoot.photos, photoId),
        });
      },
    });
  };
  const removePhotoFromTask = () => {
    if (!checkEditableWithToast() || !photoId || !task || !group) return;

    confirm({
      title: 'Remove Photo',
      message: 'Remove this photo from this task?',
      onConfirm: () => {
        scanDataMutators.updateTask(group.id, task.id, {
          photos: getScanPhotosWithoutPhotoId(task.photos, photoId),
        });
      },
    });
  };

  const setPhotoAsShownOnScopeForTask = () => {
    if (!checkEditableWithToast() || !photoId || !task || !group) return;

    const taskPhotos = task.photos ?? [];
    const existingIndex = taskPhotos.findIndex(taskPhoto => taskPhoto.photoId === photoId);

    let newTaskPhotos: ScanPhoto[];
    if (existingIndex === -1) {
      const sourcePhoto = (task.optimized.allPhotos ?? []).find(taskPhoto => taskPhoto.photoId === photoId);
      if (!sourcePhoto) return;
      newTaskPhotos = [...taskPhotos, sourcePhoto];
    } else {
      const photoToMove = taskPhotos[existingIndex];
      newTaskPhotos = [
        ...taskPhotos.slice(0, existingIndex),
        ...taskPhotos.slice(existingIndex + 1),
        photoToMove,
      ];
    }

    const photosAreUnchanged = newTaskPhotos.length === taskPhotos.length
      && newTaskPhotos.every((photo, index) => photo.photoId === taskPhotos[index]?.photoId);

    if (photosAreUnchanged) return;

    scanDataMutators.updateTask(group.id, task.id, {
      photos: newTaskPhotos,
    });
  };

  useEffect(() => {
    if (photos.length === 0) {
      closePhotos();
    }
  }, [photos, closePhotos]);

  const shownOnScopePosition = useMemo(() => {
    if (!task || !photoId) return undefined;

    const taskPhotos = task.photos ?? [];
    if (taskPhotos.length === 0) return undefined;

    const latestScopePhotos = getLatestScanPhotos(taskPhotos, 3);
    const indexInScope = latestScopePhotos.findIndex(scopePhoto => scopePhoto.photoId === photoId);
    if (indexInScope === -1) return undefined;

    return {
      index: indexInScope + 1,
      total: latestScopePhotos.length,
    };
  }, [task, photoId]);

  const isFeaturedPhoto = useMemo(() => {
    if (!scanRoot || !photoId || scanPart !== scanRoot) return undefined;
    const rootPhotos = scanRoot.photos ?? [];
    if (rootPhotos.length === 0) return undefined;
    return rootPhotos[rootPhotos.length - 1]?.photoId === photoId;
  }, [scanRoot, scanPart, photoId]);

  const setAsFeaturedPhoto = () => {
    if (!checkEditableWithToast() || !photoId || !scanRoot || scanPart !== scanRoot) return;
    const rootPhotos = scanRoot.photos ?? [];
    const existingIndex = rootPhotos.findIndex(p => p.photoId === photoId);
    if (existingIndex === -1) return;
    const photoToMove = rootPhotos[existingIndex];
    const newPhotos = [
      ...rootPhotos.slice(0, existingIndex),
      ...rootPhotos.slice(existingIndex + 1),
      photoToMove,
    ];
    scanDataMutators.updateScan({ photos: newPhotos });
  };

  const photoViewerTitle = (!group
    ? 'Exterior Property Photos'
    : groupNamePresenter(group) + (task ? ` - ${taskNamePresenter(task)}` : ''))
    + (filterDate ? ` - ${formatDate(filterDate)}` : '');

  const currentPhotoInfo = compactFlatten([
    `${currentIndex + 1} of ${photos.length}`,
    photo?.addedAt ? `added ${formatDate(photo.addedAt)}` : undefined,
    user ? `by ${user.name}` : undefined,
    photo?.photoSource ? `from ${photo.photoSource}` : undefined,
    photo?.tags ? photo.tags.map(photoTagsPresenter).join(', ') : undefined,
  ]).join(' - ');

  return (
    <div css={PHOTO_VIEWER_CONTAINER} ref={ref} {...dropZoneHandlers}>
      {isDragOver && <div css={DRAG_OVERLAY_STYLE} />}
      <div css={PHOTO_VIEWER_TITLE_SECTION}>
        <div css={PHOTO_TITLE_INFO_STYLE}>
          <div css={PHOTO_TITLE_INFO_LINE1_STYLE} title={photoViewerTitle}>
            {photoViewerTitle}
          </div>
          <div css={PHOTO_TITLE_INFO_LINE2_STYLE} title={currentPhotoInfo}>
            {currentPhotoInfo}
          </div>
        </div>
        <div css={PHOTO_TITLE_BUTTONS_STYLE}>
          <button css={[BUTTON_BASE_STYLE, CLOSE_BUTTON_STYLE]} onClick={closePhotos}>×</button>
        </div>
      </div>
      <div css={PHOTO_VIEWER_PHOTO_SECTION}>
        {photo?.photoSource === 'upload' && (
          <div css={PHOTO_VIEWER_UPLOAD_INDICATOR_STYLE}>
            <UploadIndicatorIcon size={40} />
          </div>
        )}
        {hasMultiplePhotos && (
          <>
            <button
              css={[NAV_BUTTON_STYLE, PREV_BUTTON_STYLE]}
              onClick={goToPrevious}
              title='Previous photo'
            >
              <img src={prevIcon} alt='Previous' width={53} height={71} />
            </button>
            <button
              css={[NAV_BUTTON_STYLE, NEXT_BUTTON_STYLE]}
              onClick={goToNext}
              title='Next photo'
            >
              <img src={nextIcon} alt='Next' width={53} height={71} />
            </button>
          </>
        )}
        {hasImageError
          ? (
            <div css={ERROR_IMAGE_CONTAINER_STYLE}>
              <CancelIcon size={120} color={COLORS2.alwaysDark.text.white} />
              <div css={ERROR_MESSAGE_STYLE}>Photo not found</div>
            </div>
          )
          : (
            imageUri && <ZoomableImageViewer key={photoId} imageUri={imageUri} onError={() => setHasImageError(true)} />
          )}
      </div>
      {scanIsEditable && (
        <div css={PHOTO_VIEWER_BOTTOM_SECTION}>
          <div css={PHOTO_VIEWER_BOTTOM_SECTION_LEFT_CONTENT_STYLE}>
            {
              <>
                <input
                  type='file'
                  accept='image/*'
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                />
                <Button
                  onClick={() => {
                    if (!checkEditableWithToast()) return;
                    fileInputRef.current?.click();
                  }}
                >
                  <span css={TRUNCATE_WITH_ELLIPSIS_STYLE}>
                    {task
                      ? `Add Photo to ${taskNamePresenter(task)} Task`
                      : group
                      ? `Add Photo to ${groupNamePresenter(group)} Group`
                      : 'Add Photo to Exterior Property Photos'}
                  </span>
                </Button>
              </>
            }
          </div>
          <div css={PHOTO_VIEWER_BOTTOM_SECTION_RIGHT_CONTENT_STYLE}>
            {scanPart === scanRoot && (
              <>
                <Button onClick={removeFromScanRoot}>Remove from exterior property photos</Button>
                {isFeaturedPhoto !== undefined && (
                  <Button onClick={setAsFeaturedPhoto} disabled={isFeaturedPhoto}>
                    {isFeaturedPhoto ? 'Featured Photo' : 'Set as Featured Photo'}
                  </Button>
                )}
              </>
            )}
            {group && <Button onClick={removeFromGroup}>Remove from group</Button>}
            {task && (
              <>
                <Button onClick={removePhotoFromTask}>Remove from task</Button>
                <Button onClick={setPhotoAsShownOnScopeForTask}>
                  {shownOnScopePosition
                    ? `Shown on Scope (${shownOnScopePosition.index} of ${shownOnScopePosition.total})`
                    : 'Show on Scope'}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
